// React and related imports
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

// Flowbite components
import { Card } from 'flowbite-react';

// i18next for translations
import { useTranslation } from 'react-i18next';

// Redux API hooks
import { useGetBroadcastStatsQuery } from '../redux/api/broadcastApi';

// Custom components
import { PageHeader } from '../components/Shared/PageHeader';
import { Loading } from '../components/Shared/Loading/Loading';
import { ErrorComponent } from '../components/Shared/ErrorComponent/ErrorComponent';
import { StatusBadge } from '../components/Shared/StatusBadge/StatusBadge';
import { EmptyState } from '../components/Shared/EmptyState/EmptyState';
import {
  BatchStatsPieChart,
  ProgressBar,
  UserStatItem,
} from '../components/Broadcast';

// Custom hooks
import { usePageTitle } from '../hooks/usePageTitle';

// Enums
import { BroadcastStatus } from '../common/enums/BroadcastStatus';

// Interfaces
import { IBroadcastStatsResponse } from '../common/interfaces/BroadcastStats.interface';

// Utils
import {
  getBroadcastStatusVariant,
  calculateDeliveryRate,
  calculateClickThroughRate,
  calculateBatchProgress,
} from '../components/Broadcast/utils';

export const BroadcastStatisticsPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  usePageTitle(t('pageTitles.broadcastStats'));

  const [shouldPoll, setShouldPoll] = useState(true);

  const {
    data: statsResponse,
    isLoading,
    isError,
    error: loadError,
    refetch,
  } = useGetBroadcastStatsQuery(id || '', {
    pollingInterval: shouldPoll ? 2000 : 0, // Poll every 2 seconds only if shouldPoll is true
    refetchOnMountOrArgChange: true, // Fetch fresh data on mount and refresh
  });

  const stats =
    (statsResponse as IBroadcastStatsResponse | undefined)?.data ||
    statsResponse;

  // Check if broadcast is in terminal state and update polling state
  useEffect(() => {
    if (stats) {
      const isTerminalState =
        stats.status === BroadcastStatus.COMPLETED ||
        stats.status === BroadcastStatus.FAILED;
      setShouldPoll(!isTerminalState);
    }
  }, [stats]);

  const handleBack = () => {
    navigate('/broadcast');
  };

  if (isLoading) return <Loading />;
  if (isError) return <ErrorComponent error={loadError} onRetry={refetch} />;
  if (!stats || !stats.users || !stats.batches) {
    return <EmptyState title={t('broadcast.noStats')} />;
  }

  // Cumulative display values: each tier includes all downstream interactions
  const displayedClicked = stats.users.clicked;
  const displayedReceived = stats.users.received + displayedClicked;
  const displayedSent = stats.users.sent + displayedReceived;

  const deliveryRate = calculateDeliveryRate(displayedReceived, displayedSent);

  const clickThroughRate = calculateClickThroughRate(
    displayedClicked,
    displayedReceived
  );

  const batchProgress = calculateBatchProgress(
    stats.batches.sent,
    stats.batches.failed,
    stats.batches.total
  );

  return (
    <>
      <PageHeader
        title={t('broadcast.statistics')}
        buttonText={t('common.back')}
        buttonOnClick={handleBack}
      />

      <div className="space-y-6 pb-8">
        {/* Row 1: Overview with Status, Total Users, Total Batches, and Progress Bar */}
        <Card>
          <h3 className="mb-4 text-lg font-semibold text-gray-900">
            {t('broadcast.overview')}
          </h3>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            {/* Status */}
            <div className="flex flex-col items-center justify-center rounded-lg border border-gray-200 bg-gray-50 p-4">
              <span className="mb-2 text-sm text-gray-600">
                {t('broadcast.statusColumn')}
              </span>
              <StatusBadge
                variant={getBroadcastStatusVariant(
                  stats.status as BroadcastStatus
                )}
                size="md"
              >
                {t(`broadcast.status.${stats.status}`)}
              </StatusBadge>
            </div>

            {/* Total Users */}
            <div className="flex flex-col items-center justify-center rounded-lg border border-gray-200 bg-gray-50 p-4">
              <span className="mb-2 text-sm text-gray-600">
                {t('broadcast.totalUsers')}
              </span>
              <span className="text-3xl font-bold text-gray-900">
                {stats.totalUsers.toLocaleString()}
              </span>
            </div>

            {/* Total Batches */}
            <div className="flex flex-col items-center justify-center rounded-lg border border-gray-200 bg-gray-50 p-4">
              <span className="mb-2 text-sm text-gray-600">
                {t('broadcast.totalBatches')}
              </span>
              <span className="text-3xl font-bold text-gray-900">
                {stats.batches.total}
              </span>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="mt-6">
            <ProgressBar
              percentage={batchProgress}
              label={t('broadcast.batchProgress')}
              status={stats.status}
            />
          </div>
        </Card>

        {/* Row 2: Batch Statistics Pie Chart and User Engagement Stats */}
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          {/* Batch Statistics Pie Chart */}
          <Card>
            <h3 className="mb-4 text-center text-lg font-semibold text-gray-900">
              {t('broadcast.batchStats')}
            </h3>
            <BatchStatsPieChart
              scheduled={stats.batches.scheduled}
              processing={stats.batches.processing}
              sent={stats.batches.sent}
              failed={stats.batches.failed}
              total={stats.batches.total}
            />
          </Card>

          {/* User Engagement Stats */}
          <Card>
            <h3 className="mb-4 text-center text-lg font-semibold text-gray-900">
              {t('broadcast.userEngagement')}
            </h3>
            <div
              className="flex flex-col justify-center space-y-4"
              style={{ height: '300px' }}
            >
              <UserStatItem
                label={t('broadcast.sent')}
                value={displayedSent}
                color="primary"
              />
              <UserStatItem
                label={t('broadcast.received')}
                value={displayedReceived}
                color="success"
              />
              <UserStatItem
                label={t('broadcast.clicked')}
                value={displayedClicked}
                color="accent"
                showBorder={false}
              />
            </div>
          </Card>
        </div>

        {/* Row 3: Delivery Rate and Click-Through Rate */}
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          {/* Delivery Rate Card */}
          <Card>
            <h3 className="mb-4 text-center text-lg font-semibold text-gray-900">
              {t('broadcast.deliveryRate')}
            </h3>
            <div className="flex flex-col items-center justify-center py-8">
              <div className="text-6xl font-bold text-success-600 dark:text-success-400">
                {deliveryRate}%
              </div>
              <p className="mt-4 text-center text-gray-600">
                {displayedReceived.toLocaleString()} /{' '}
                {displayedSent.toLocaleString()} {t('broadcast.usersReceived')}
              </p>
            </div>
          </Card>

          {/* Click-Through Rate Card */}
          <Card>
            <h3 className="mb-4 text-center text-lg font-semibold text-gray-900">
              {t('broadcast.clickThroughRate')}
            </h3>
            <div className="flex flex-col items-center justify-center py-8">
              <div className="text-6xl font-bold text-primary-600 dark:text-primary-300">
                {clickThroughRate}%
              </div>
              <p className="mt-4 text-center text-gray-600">
                {displayedClicked.toLocaleString()} /{' '}
                {displayedSent.toLocaleString()} {t('broadcast.usersClicked')}
                {/* {displayedReceived.toLocaleString()} */}
              </p>
            </div>
          </Card>
        </div>
      </div>
    </>
  );
};
